import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Client } from '@/api/clients/clientsApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/common/Icon';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import {
  useInviteClientMutation,
  useRevokeClientInviteMutation,
} from '@/hooks/useClientMutations';
import { copyTextToClipboard } from '@/lib/clipboard';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface InvitePanelProps {
  client: Client;
}

export function InvitePanel({ client }: InvitePanelProps) {
  const { t } = useTranslation('clients');
  const inviteMutation = useInviteClientMutation();
  const revokeMutation = useRevokeClientInviteMutation();
  const [isConfirmingRevoke, setIsConfirmingRevoke] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const hasEmail = client.email !== null && client.email.trim().length > 0;
  const isBusy = inviteMutation.isPending || revokeMutation.isPending;

  function reportError(actionError: unknown) {
    const backendMessage = resolveApiErrorMessage(actionError);
    showErrorNotification(backendMessage.length > 0 ? backendMessage : t('invite.error'));
  }

  function sendInvitation() {
    inviteMutation.mutate(client.id, {
      onSuccess: function done(result) {
        setInviteLink(result.inviteLink);
        showSuccessNotification(t('invite.sent'));
      },
      onError: reportError,
    });
  }

  async function copyInviteLink() {
    if (inviteLink === null) {
      return;
    }
    const didCopy = await copyTextToClipboard(inviteLink);
    showSuccessNotification(didCopy === true ? t('invite.linkCopied') : t('invite.copyFailed'));
  }

  function confirmRevoke() {
    revokeMutation.mutate(client.id, {
      onSuccess: function done() {
        setInviteLink(null);
        setIsConfirmingRevoke(false);
      },
      onError: function fail(revokeError) {
        reportError(revokeError);
        setIsConfirmingRevoke(false);
      },
    });
  }

  return (
    <Card>
      <h2 className="text-sm font-bold text-zooindigo mb-2">{t('invite.title')}</h2>

      {client.portalStatus === 'registered' ? (
        <p className="text-sm text-zoosuccesstext flex items-center gap-2">
          <Icon name="CheckCircle" /> {t('invite.registered')}
        </p>
      ) : client.portalStatus === 'invited' ? (
        <>
          <p className="text-sm text-zoomuted">{t('invite.invited')}</p>
          <InvitationExpiry expiresAt={client.portalInvitationExpiresAt} />
          <div className="flex flex-wrap gap-2">
            <Button
              size="small"
              variant="outline"
              disabled={isBusy}
              isLoading={inviteMutation.isPending}
              onClick={sendInvitation}
            >
              {inviteMutation.isPending ? null : <Icon name="RefreshCw" />} {t('invite.resend')}
            </Button>
            <Button
              size="small"
              variant="outline"
              disabled={isBusy}
              className="text-zooerrortext"
              onClick={function openRevoke() {
                setIsConfirmingRevoke(true);
              }}
            >
              {t('invite.revoke')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-zoomuted mb-3">
            {hasEmail === true ? t('invite.notInvited') : t('invite.needsEmail')}
          </p>
          <Button
            size="small"
            disabled={hasEmail === false}
            isLoading={inviteMutation.isPending}
            onClick={sendInvitation}
          >
            {inviteMutation.isPending ? null : <Icon name="Send" />} {t('invite.invite')}
          </Button>
        </>
      )}

      {inviteLink !== null ? (
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="invite-link">{t('invite.linkLabel')}</Label>
          <div className="flex gap-2">
            <Input
              id="invite-link"
              readOnly
              value={inviteLink}
              className="text-xs"
              onFocus={function selectAll(focusEvent) {
                focusEvent.currentTarget.select();
              }}
            />
            <Button size="small" variant="outline" onClick={copyInviteLink}>
              <Icon name="Link" /> {t('invite.copyLink')}
            </Button>
          </div>
          <p className="text-[11px] text-zoomuted">{t('invite.linkHint')}</p>
        </div>
      ) : null}

      {isConfirmingRevoke === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('invite.revoke')}
          description={t('invite.revokeConfirm')}
          confirmLabel={t('invite.revoke')}
          isConfirming={revokeMutation.isPending}
          onConfirm={confirmRevoke}
          onCancel={function cancel() {
            setIsConfirmingRevoke(false);
          }}
        />
      ) : null}
    </Card>
  );
}

function InvitationExpiry({ expiresAt }: { expiresAt: string | null }) {
  const { t } = useTranslation('clients');
  if (expiresAt === null) {
    return <div className="mb-3" />;
  }
  const hasExpired = new Date(expiresAt).getTime() < Date.now();
  return (
    <p
      className={
        'text-xs mt-1 mb-3 ' + (hasExpired ? 'text-zooerrortext font-semibold' : 'text-zoomuted')
      }
    >
      {hasExpired
        ? t('invite.expired')
        : t('invite.expiresOn', { date: formatIsoDateTimeToTimestamp(expiresAt) })}
    </p>
  );
}
